import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useAuthStore, NoNestAccessError } from '@/stores/authStore'
import { consumeReturnTo, consumeState } from '@/lib/oauth'

// Cognito hosted-UI OAuth redirect target (registered as a callback on the
// phenom-prod-nest-spa client). Exchanges the authorization code for tokens,
// enforces the nest-access group, then returns the user where they started.
// Outside _authenticated so the guard doesn't bounce us before the exchange.
export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
})

function AuthCallback() {
  const completeAuth = useAuthStore((s) => s.completeAuth)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthError = params.get('error')
    const code = params.get('code')
    if (oauthError) {
      setError(params.get('error_description') || oauthError)
      return
    }
    if (!code) {
      setError('Missing authorization code.')
      return
    }
    // CSRF/fixation gate (#451): the `state` Cognito echoes back must equal the
    // nonce we stored at beginLogin. A missing or mismatched state means this
    // callback was not initiated by this browser — reject before any exchange.
    const stored = consumeState()
    const returned = params.get('state')
    if (!stored || !returned || stored !== returned) {
      setError('Sign-in could not be verified (state mismatch). Please start again.')
      return
    }
    completeAuth(code)
      .then(() => {
        // Full navigation so any same-origin proxied paths resolve cleanly.
        window.location.replace(consumeReturnTo())
      })
      .catch((e: unknown) => {
        if (e instanceof NoNestAccessError) {
          setError(
            'Your account is not authorized to access N.E.S.T. ' +
              'Contact an administrator to request access.',
          )
        } else {
          setError(e instanceof Error ? e.message : 'Sign-in failed.')
        }
      })
  }, [completeAuth])

  return (
    <div className='flex h-svh items-center justify-center p-8'>
      {error ? (
        <div className='max-w-md space-y-3 text-center'>
          <p className='text-destructive text-sm'>{error}</p>
          <a href='/sign-in' className='text-sm underline hover:opacity-75'>
            Back to sign in
          </a>
        </div>
      ) : (
        <p className='text-muted-foreground text-sm'>Signing you in…</p>
      )}
    </div>
  )
}
